/**
 * Shared form body for both /admin/campaigns/new and /admin/campaigns/:id/edit.
 *
 * - Owns no submit logic itself — the parent wires that.
 * - In `create` mode persists in-progress values to localStorage so a refresh
 *   doesn't lose work. `edit` mode skips localStorage (we want pristine load
 *   from the server every time).
 * - datetime-local input ALWAYS displays in the browser's local timezone, so
 *   the "Scheduled at" field is in the user's local timezone. We render the
 *   detected zone label next to the field to make this unambiguous, and the
 *   "Run now" preset clears the issue entirely for the common case.
 */
import React, { useEffect, useState } from "react";
import {
  AudienceSpec,
  Campaign,
  CampaignPayload,
  CreateCampaignInput,
  FieldUpdate,
  Platform,
  decodeAudienceSpec,
  decodePayload,
} from "../../../api/campaigns";

type FieldKey = "position_header" | "position_one" | "position_two";

const DEFAULT_FIELDS: Record<FieldKey, FieldUpdate> = {
  position_header: { label: "Balance", value: "", changeMessage: "" },
  position_one: { label: "", value: "", changeMessage: "" },
  position_two: { label: "", value: "", changeMessage: "" },
};

const FORM_DRAFT_KEY = "add2wallet_campaign_draft_form";

interface PersistedForm {
  name?: string;
  merchantId?: string;
  platform?: Platform;
  audienceType?: "merchant_all" | "keywords";
  audienceCampaignId?: string;
  keywordsText?: string;
  fields?: Record<string, FieldUpdate>;
  fieldEnabled?: Record<string, boolean>;
  scheduledAt?: string;
}

interface Props {
  mode: "create" | "edit";
  /** When mode=edit, the existing campaign to populate the form. */
  initial?: Campaign;
  /** Called on submit with the assembled payload. */
  onSubmit: (input: CreateCampaignInput) => Promise<void>;
  /** Optional cancel handler — defaults to history.back. */
  onCancel?: () => void;
  /** Label on the submit button (e.g. "Create draft" vs "Save changes"). */
  submitLabel: string;
}

const CampaignForm: React.FC<Props> = ({
  mode,
  initial,
  onSubmit,
  onCancel,
  submitLabel,
}) => {
  // Detect the browser's resolved timezone — shown next to the time input.
  const localTz =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "your local time";
  const tzOffsetLabel = formatTzOffset(new Date().getTimezoneOffset());

  const initialFromCampaign = mode === "edit" && initial ? campaignToFormState(initial) : null;
  const persisted: PersistedForm =
    mode === "create"
      ? (() => {
          try {
            const raw = localStorage.getItem(FORM_DRAFT_KEY);
            return raw ? JSON.parse(raw) : {};
          } catch {
            return {};
          }
        })()
      : {};

  const initialState: PersistedForm =
    initialFromCampaign ??
    {
      ...persisted,
      merchantId:
        persisted.merchantId ??
        localStorage.getItem("add2wallet_default_merchant") ??
        "76",
    };

  const [name, setName] = useState(initialState.name ?? "");
  const [merchantId, setMerchantId] = useState(initialState.merchantId ?? "76");
  const [platform, setPlatform] = useState<Platform>(
    initialState.platform ?? "both"
  );
  const [audienceType, setAudienceType] = useState<"merchant_all" | "keywords">(
    initialState.audienceType ?? "merchant_all"
  );
  const [audienceCampaignId, setAudienceCampaignId] = useState(
    initialState.audienceCampaignId ?? ""
  );
  const [keywordsText, setKeywordsText] = useState(initialState.keywordsText ?? "");
  const [fields, setFields] = useState<Record<FieldKey, FieldUpdate>>(
    (initialState.fields as Record<FieldKey, FieldUpdate>) ?? DEFAULT_FIELDS
  );
  const [fieldEnabled, setFieldEnabled] = useState<Record<FieldKey, boolean>>(
    (initialState.fieldEnabled as Record<FieldKey, boolean>) ?? {
      position_header: true,
      position_one: false,
      position_two: false,
    }
  );
  const [scheduledAt, setScheduledAt] = useState(
    initialState.scheduledAt ?? defaultScheduledAtLocal()
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // localStorage persistence — create mode only. In edit mode we want every
  // visit to start from the server's current state, not a stale draft.
  useEffect(() => {
    if (mode !== "create") return;
    const snapshot: PersistedForm = {
      name,
      merchantId,
      platform,
      audienceType,
      audienceCampaignId,
      keywordsText,
      fields,
      fieldEnabled,
      scheduledAt,
    };
    try {
      localStorage.setItem(FORM_DRAFT_KEY, JSON.stringify(snapshot));
    } catch {}
  }, [
    mode,
    name,
    merchantId,
    platform,
    audienceType,
    audienceCampaignId,
    keywordsText,
    fields,
    fieldEnabled,
    scheduledAt,
  ]);

  function updateField(key: FieldKey, patch: Partial<FieldUpdate>) {
    setFields((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      let audience_spec: AudienceSpec;
      if (audienceType === "merchant_all") {
        audience_spec = audienceCampaignId
          ? { merchant_id: merchantId, campaign_id: audienceCampaignId }
          : { merchant_id: merchantId };
      } else {
        const kws = keywordsText
          .split(/[\s,]+/)
          .map((k) => k.trim())
          .filter(Boolean);
        audience_spec = { keywords: kws };
      }

      const payload: CampaignPayload = {};
      (Object.keys(fields) as FieldKey[]).forEach((k) => {
        if (fieldEnabled[k]) {
          const f = fields[k];
          payload[k] = {
            label: f.label,
            value: f.value,
            ...(f.changeMessage ? { changeMessage: f.changeMessage } : {}),
          };
        }
      });

      let scheduledIso = "";
      if (scheduledAt) {
        try {
          scheduledIso = new Date(scheduledAt).toISOString();
        } catch {
          scheduledIso = "";
        }
      }

      const input: CreateCampaignInput = {
        merchant_id: merchantId,
        name: name.trim(),
        audience_type: audienceType,
        audience_spec,
        payload,
        platform,
        scheduled_at: scheduledIso,
      };

      await onSubmit(input);
      if (mode === "create") {
        try {
          localStorage.removeItem(FORM_DRAFT_KEY);
        } catch {}
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || "Submit failed"
      );
    } finally {
      setSubmitting(false);
    }
  }

  function setRunNow() {
    // 90 seconds in the future — gives the dispatcher one tick to pick it up.
    const d = new Date(Date.now() + 90_000);
    setScheduledAt(toLocalDateTimeInputValue(d));
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basics */}
      <section className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold">Basics</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Campaign name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Easter Bonus 2026"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Merchant ID
            </label>
            <input
              type="text"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="both">Both</option>
              <option value="apple">Apple only</option>
              <option value="google">Google only</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Scheduled to fire at
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              type="button"
              onClick={setRunNow}
              className="px-3 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 text-sm whitespace-nowrap"
              title="Set to ~90 seconds from now"
            >
              Run now
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Time shown in <strong>{localTz}</strong> ({tzOffsetLabel}). The
            dispatcher polls every minute, so a campaign fires within ~60s of
            the scheduled time.
          </p>
        </div>
      </section>

      {/* Audience */}
      <section className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold">Audience</h2>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={audienceType === "merchant_all"}
              onChange={() => setAudienceType("merchant_all")}
            />
            All passes for this merchant
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              checked={audienceType === "keywords"}
              onChange={() => setAudienceType("keywords")}
            />
            Explicit keyword list
          </label>
        </div>

        {audienceType === "merchant_all" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Narrow to one campaign_id (optional)
            </label>
            <input
              type="text"
              value={audienceCampaignId}
              onChange={(e) => setAudienceCampaignId(e.target.value)}
              placeholder='e.g. 0 for general — leave blank for all'
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Filters by <code>campaign_id</code> on the GSI. String values
              only (e.g. <code>"0"</code>, not <code>0</code>).
            </p>
          </div>
        )}

        {audienceType === "keywords" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keywords
            </label>
            <textarea
              rows={4}
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              placeholder="hHzvChXf, 2MO6nkAO, RIusasqd&#10;(comma- or newline-separated)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
            />
          </div>
        )}
      </section>

      {/* Pass payload */}
      <section className="bg-white rounded-lg shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold">Pass field updates</h2>
        <p className="text-sm text-gray-500">
          Each field maps onto Apple Wallet's header / primary / secondary
          field and Google Wallet's loyalty points / points module.{" "}
          <strong>changeMessage</strong> drives the lock-screen banner —{" "}
          <code>%@</code> is replaced by the new value.
        </p>
        {(Object.keys(fields) as FieldKey[]).map((k) => (
          <div
            key={k}
            className={`border rounded-md p-4 ${fieldEnabled[k] ? "border-purple-300 bg-purple-50/30" : "border-gray-200"}`}
          >
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={fieldEnabled[k]}
                onChange={(e) =>
                  setFieldEnabled((prev) => ({
                    ...prev,
                    [k]: e.target.checked,
                  }))
                }
              />
              <span className="text-sm font-medium font-mono">{k}</span>
            </label>
            {fieldEnabled[k] && (
              <div className="space-y-2 pl-6">
                <input
                  type="text"
                  value={fields[k].label}
                  onChange={(e) => updateField(k, { label: e.target.value })}
                  placeholder="Label (e.g. Balance)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={fields[k].value}
                  onChange={(e) => updateField(k, { value: e.target.value })}
                  placeholder="Value (e.g. $150.00)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="text"
                  value={fields[k].changeMessage || ""}
                  onChange={(e) =>
                    updateField(k, { changeMessage: e.target.value })
                  }
                  placeholder="changeMessage — e.g. Balance is now %@"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            )}
          </div>
        ))}
      </section>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel || (() => window.history.back())}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 disabled:bg-gray-300"
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
};

// ----- helpers -----

/** Default schedule = now + 1 hour, formatted for datetime-local in LOCAL time. */
function defaultScheduledAtLocal(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  return toLocalDateTimeInputValue(d);
}

/**
 * Format a Date as a `YYYY-MM-DDTHH:mm` string in the browser's local
 * timezone. This is what <input type="datetime-local"> expects.
 *
 * Don't use `.toISOString().slice(0, 16)` — that returns UTC, which the input
 * then displays as if it were local. Confusing for anyone outside UTC.
 */
function toLocalDateTimeInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

/** "+10:00" / "-05:30" from Date.getTimezoneOffset(). */
function formatTzOffset(minutes: number): string {
  // getTimezoneOffset returns positive west of UTC, negative east. Flip for display.
  const totalMin = -minutes;
  const sign = totalMin >= 0 ? "+" : "-";
  const abs = Math.abs(totalMin);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function campaignToFormState(c: Campaign): PersistedForm {
  const audience = decodeAudienceSpec(c);
  const payload = decodePayload(c) || {};
  const fields: Record<FieldKey, FieldUpdate> = {
    position_header: payload.position_header || {
      label: "Balance",
      value: "",
      changeMessage: "",
    },
    position_one: payload.position_one || { label: "", value: "", changeMessage: "" },
    position_two: payload.position_two || { label: "", value: "", changeMessage: "" },
  };
  const fieldEnabled: Record<FieldKey, boolean> = {
    position_header: !!payload.position_header,
    position_one: !!payload.position_one,
    position_two: !!payload.position_two,
  };

  let scheduledAt = "";
  if (c.scheduled_at) {
    try {
      scheduledAt = toLocalDateTimeInputValue(new Date(c.scheduled_at));
    } catch {
      scheduledAt = "";
    }
  }

  let audienceCampaignId = "";
  let keywordsText = "";
  if (audience) {
    if ("campaign_id" in audience && audience.campaign_id) {
      audienceCampaignId = audience.campaign_id;
    }
    if ("keywords" in audience && Array.isArray(audience.keywords)) {
      keywordsText = audience.keywords.join(", ");
    }
  }

  return {
    name: c.name,
    merchantId: c.merchant_id,
    platform: c.platform,
    audienceType: c.audience_type,
    audienceCampaignId,
    keywordsText,
    fields,
    fieldEnabled,
    scheduledAt,
  };
}

export default CampaignForm;
